import _ from 'lodash';
import initialState from '../initialstate';
import * as types from '../../actions/actiontypes';
import Utility from '../../util/utilityfunctions';
import {HttpStatusCodes, reviewNoteStatus, entities, rnhNoteType, filters} from '../../util/uiconstants';

/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      storeIgua.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */
export default function reviewNotesReducer(state = initialState.rnhNotes, action) {
	switch (action.type) {
		case types.GET_RNH_NOTES: {
			return action.response;
		}
		case types.UPDATE_RNH_NOTES_DATA: {
			if (!state || !state.annotations) return state;

			let newState = _.cloneDeep(state);
			let element = getAnnotation(newState, action.newNote.id, action.isGrouped);

			if (element) {
				element.createUserId = action.newNote.createUserId;
				element.createUserName = action.newNote.createUserName;
				element.dueDate = action.newNote.dueDate;
				element.assignedToUserId = action.newNote.assignedToUserId;
				element.assignedToUserName = action.newNote.assignedToUserName;
				element.statusId = action.newNote.statusId;
				element.priorityTypeId = action.newNote.priorityTypeId;
				element.text = action.newNote.text;
				element.modifiedDate = action.newNote.modifiedDate;
				element.replyCount = action.newNote.replies ? action.newNote.replies.length : 0;
				element.assignedToUserInitials = action.newNote.assignedToUserInitials;
				element.annotationAttribute = action.newNote.annotationAttribute;
				return newState;
			} else {
				return state;
			}
		}
		case types.CLEAN_RNH_NOTES: {
			return {};
		}
		case types.EMPTY_RNH_NOTES: {
			return {
				allOpenReviewNotesCount: 0,
				allClosedReviewNotesCount: 0,
				allClearedReviewNotesCount: 0,
				totalReviewNotesCount: 0,
				myOpenReviewNotesCount: 0,
				countOverdue: 0,
				totalCount: 0,
				documentOpenReviewNoteCount: 0,
				documentMyOpenReviewNoteCount: 0,
				otherEngagementsReviewNoteCount: 0,
				availablePageCount: 0,
				isOtherEngagementsReviewNotePresent: false,
				annotations: []
			};
		}
		case types.UPDATE_RNH_NOTES_BULK: {
			if (action.responseObj.status === HttpStatusCodes.Ok) {
				let newState = _.cloneDeep(state);
				const userId = action.body.toUser;
				const userName = action.userName;
				const userInitials = action.userInitials;
				const priorityTypeId = action.body.priorityTypeId;
				const annotationStatusId = action.sendCommentData
					? Utility.getAnnotationIdFromComment(action.body.annotationStatusId)
					: action.body.annotationStatusId;
				const dueDate = action.body.dueDate;
				const modifiedDate = action.body.modifiedDate;

				// iterate through annotations list. Is the outter loop because annotations count > selected ids count
				_.forEach(newState.annotations, (a) => {
					let element;
					// iterate through selected annotations ids
					_.forEach(action.body.ids, (id) => {
						if (action.hasGroups && a.responses) {
							element = _.find(a.responses, (r) => r.id === id);
						} else {
							if (a.id === id) {
								element = a; // element is the annotation who has not responses
							}
						}
						if (element) {
							if (userId && element.assignedToUserName !== userId) {
								element.assignedToUserId = userId;
								element.assignedToUserName = userName;
								element.assignedToUserInitials = userInitials;
							}
							if (priorityTypeId && element.priorityTypeId !== priorityTypeId) {
								element.priorityTypeId = priorityTypeId;
							}
							if (annotationStatusId && element.statusId !== annotationStatusId) {
								// switchs to silent refresh the counts in the left pane
								// decrease the element current status
								switch (element.statusId) {
									case reviewNoteStatus.Open.id:
										newState.allOpenReviewNotesCount--;
										break;
									case reviewNoteStatus.Closed.id:
										if(annotationStatusId !== reviewNoteStatus.Cleared.id)
											newState.allClosedReviewNotesCount--;
										break;
									case reviewNoteStatus.Cleared.id:
										newState.allClearedReviewNotesCount--;
										break;
									default:
										break;
								}
								// then increase the selected status
								switch (annotationStatusId) {
									case reviewNoteStatus.Open.id:
										newState.allOpenReviewNotesCount++;
										break;
									case reviewNoteStatus.Closed.id:
										newState.allClosedReviewNotesCount++;
										break;
									case reviewNoteStatus.Cleared.id:
										if(element.statusId !== reviewNoteStatus.Closed.id)
											newState.allClearedReviewNotesCount++;
										break;
									default:
										break;
								}
								
								if(element.statusId !== reviewNoteStatus.Closed.id || (element.statusId === reviewNoteStatus.Closed.id && annotationStatusId === reviewNoteStatus.Open.id))
									element.statusId = annotationStatusId;

							}
							if (dueDate) {
								element.dueDate = dueDate;
							}
							element.modifiedDate = modifiedDate;
						}
					});
				});

				return newState;
			}
			return state;
		}
		case types.UPDATE_RNH_NOTE_DETAIL: {
			if (action.note === undefined) return state;

			let newState = _.cloneDeep(state);
			let selectedNoteList = newState.annotations;
			let element;
			for (let i = 0; i < selectedNoteList.length; i++) {
				// For Document/Task Category
				if (
					action.hasGroups &&
					selectedNoteList[i].responses !== null &&
					selectedNoteList[i].entityUID === action.selectedNote.entityuid
				) {
					element = selectedNoteList[i].responses.find((note) => note.id === action.selectedNote.noteId);
				}
				// For All Notes Category
				else {
					element = selectedNoteList[i];
				}

				if (element.id === action.selectedNote.noteId) {
					if (!_.isEmpty(action.userInputs.toUser)) {
						element.assignedToUserName = action.userInputs.toUser.label;
						element.assignedToUserId = action.userInputs.toUser.value;
					}
					if (!_.isEmpty(action.userInputs.priority)) {
						element.priorityTypeId = action.userInputs.priority.value;
					}

					if (action.userInputs.dueDate !== '') {
						element.dueDate = action.userInputs.dueDate.toISOString();
					}

					element.text = action.userInputs.comments;
					element.modifiedDate = new Date().toISOString();
					break;
				}
			}

			return Object.assign({}, state, newState);
		}
		case types.DELETE_RNH_NOTE_FROM_LIST: {
			let newState = _.cloneDeep(state);
			const annotationId = action.annotationId;
			const hasGroups = action.hasGroups;

			return deleteAnnotation(newState, annotationId, hasGroups);
		}
		case types.UPDATE_RNH_NOTES_COUNT: {
			if (!state || !state.annotations) return state;

			let newState = _.cloneDeep(state);
			let element = getAnnotation(newState, action.noteId, action.hasGroups);

			if (element) {
				// decrease count
				switch (action.oldStatusId) {
					case reviewNoteStatus.Open.id:
						newState.allOpenReviewNotesCount--;
						break;
					case reviewNoteStatus.Closed.id:
						newState.allClosedReviewNotesCount--;
						break;
					case reviewNoteStatus.Cleared.id:
						newState.allClearedReviewNotesCount--;
						break;
					default:
						break;
				}
				// increase count
				switch (action.statusId) {
					case reviewNoteStatus.Open.id:
						newState.allOpenReviewNotesCount++;
						break;
					case reviewNoteStatus.Closed.id:
						newState.allClosedReviewNotesCount++;
						break;
					case reviewNoteStatus.Cleared.id:
						newState.allClearedReviewNotesCount++;
						break;
					default:
						break;
				}

				return newState;
			} else {
				return state;
			}
		}
		case types.DELETE_RNH_NOTE: {
			if (action.returnCurrentState) return state;

			let clonedState = _.cloneDeep(state);
			for (let i = 0; i < action.noteIds.length; i++) {
				clonedState = deleteAnnotation(clonedState, action.noteIds[i], action.hasGroups);
			}

			return clonedState;
		}
		case types.ADD_RNH_NOTE_FROM_LIST: {
			if (!state || !state.annotations) return state;

			let newState = _.cloneDeep(state);
			let entityIndex = newState.annotations.findIndex(
				(noteGroup) => noteGroup.entityUID === action.noteInfo.entityUID
			);

			newState.allOpenReviewNotesCount++;
			if (
				action.noteInfo.currentUser === action.noteInfo.fromUser ||
				action.noteInfo.currentUser === action.noteInfo.toUser
			) {
				newState.myOpenReviewNotesCount++;
			}

			newState.annotations[entityIndex].responses.push({
				annotationTypeId: action.noteInfo.annotationTypeId,
				assignedToUserName: action.noteInfo.toUserName,
				priorityTypeId: action.noteInfo.priorityTypeId,
				createUserName: action.noteInfo.fromUserName,
				assignedToUserId: action.noteInfo.toUser,
				createUserId: action.noteInfo.fromUser,
				entityUID: action.noteInfo.entityUID,
				entityID: action.noteInfo.entityID,
				dueDate: action.noteInfo.dueDate,
				text: action.noteInfo.comments,
				id: action.responseObj.data.id,
				statusId: action.responseObj.data.statusId,
				createDate: action.responseObj.data.createDate,
				modifiedDate: action.responseObj.data.createDate,
				referenceNumber: action.responseObj.data.referenceNumber,
				editable: action.noteInfo.currentUser === action.noteInfo.fromUser,
				createUserInitials: action.noteInfo.createUserInitials,
				assignedToUserInitials: action.noteInfo.assignedToUserInitials
			});
			
			return newState;
		}
		default:
			return state;
	}
}

const getAnnotation = (newState, noteId, hasGroups) => {
	let element;

	if (newState && newState.annotations) {
		if (hasGroups) {
			newState.annotations.every((a) => {
				element = a.responses.find((r) => r.id === noteId);
				if (element) {
					return false;
				}
				return true;
			});
		} else {
			element = newState.annotations.find((a) => {
				return a.id === noteId;
			});
		}
	}

	return element;
};

const deleteAnnotation = (newState, annotationId, hasGroups) => {
	let element = getAnnotation(newState, annotationId, hasGroups);
	if (element) {
		switch (element.statusId) {
			case reviewNoteStatus.Open.id:
				newState.allOpenReviewNotesCount--;
				break;
			case reviewNoteStatus.Closed.id:
				newState.allClosedReviewNotesCount--;
				break;
			case reviewNoteStatus.Cleared.id:
				newState.allClearedReviewNotesCount--;
				break;
			default:
				break;
		}

		let removedElement = [];
		if (hasGroups) {
			// get parent annotation
			let parent = _.find(newState.annotations, (a) => {
				return a.entityUID === element.entityUID;
			});

			// delete note from annotation (response) list
			removedElement = parent.responses ? _.remove(parent.responses, (r) => r.id === annotationId) : [];

			// if parent does not have annotation, remove parent
			if (_.isEmpty(parent.responses)) {
				_.remove(newState.annotations, (a) => {
					return a.id === parent.id;
				});
			}
		} else {
			// delete note from annotation list
			removedElement = _.remove(newState.annotations, (a) => {
				return a.id === annotationId;
			});
		}

		// fix total count
		newState.totalReviewNotesCount = _.isEmpty(newState.annotations) ? 0 : newState.annotations?.length;
	}

	return newState;
};
